import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionHeader from '../ui/SectionHeader';
import { fadeUp, staggerContainer } from '../../utils/motionPresets';

const Section = styled.section`
  padding: 96px 0;
  background: #f7fafc;
  overflow: hidden;
`;

const Track = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
`;

const Connector = styled.div`
  display: none;

  @media (min-width: 969px) {
    display: block;
    position: absolute;
    top: 56px;
    left: 10%;
    right: 10%;
    height: 2px;
    border-top: 2px dashed rgba(14, 107, 60, 0.22);
    z-index: 0;
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  position: relative;
  z-index: 1;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    max-width: 360px;
    margin: 0 auto;
  }
`;

const Card = styled(motion.article)`
  background: #fff;
  border: 1px solid #e8eef3;
  border-radius: 16px;
  padding: 28px 18px 24px;
  text-align: center;
  box-shadow: 0 2px 14px rgba(15, 23, 42, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(14, 107, 60, 0.25);
    box-shadow: 0 16px 40px rgba(14, 107, 60, 0.12);
  }
`;

const StepNum = styled.span`
  display: block;
  font-family: var(--font-display);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #6dbb1a;
  margin-bottom: 14px;
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 16px;
  border-radius: 14px;
  border: 1.5px solid rgba(14, 107, 60, 0.18);
  background: rgba(14, 107, 60, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0e6b3c;
  font-size: 1.2rem;
`;

const Title = styled.h3`
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.55;
  margin: 0;
`;

const steps = [
  {
    num: '01',
    icon: 'fas fa-search',
    title: 'Discover',
    desc: 'We learn your business goals, users, and technical needs through workshops and research.',
  },
  {
    num: '02',
    icon: 'fas fa-clipboard-list',
    title: 'Plan',
    desc: 'Scope, timeline, milestones, and tech stack are defined in a clear project roadmap.',
  },
  {
    num: '03',
    icon: 'fas fa-pencil-ruler',
    title: 'Design',
    desc: 'Wireframes and UI mockups are crafted for approval before development starts.',
  },
  {
    num: '04',
    icon: 'fas fa-code',
    title: 'Develop',
    desc: 'Agile sprints build your product with clean code and regular progress updates.',
  },
  {
    num: '05',
    icon: 'fas fa-vial',
    title: 'Test',
    desc: 'QA, performance checks, and bug fixes ensure a stable, polished experience.',
  },
  {
    num: '06',
    icon: 'fas fa-rocket',
    title: 'Launch',
    desc: 'We deploy, train your team, and provide ongoing support after go-live.',
  },
];

const ProcessSection = () => (
  <Section id="process">
    <div className="container">
      <SectionHeader
        label="Our Process"
        title="Our Development Process"
        subtitle="A proven six-step workflow that delivers quality software on time and on budget."
      />
      <Track>
        <Connector aria-hidden="true" />
        <Grid
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {steps.map((s, i) => (
            <Card key={s.title} variants={fadeUp} custom={i}>
              <StepNum>{s.num}</StepNum>
              <IconWrap>
                <i className={s.icon} aria-hidden="true" />
              </IconWrap>
              <Title>{s.title}</Title>
              <Desc>{s.desc}</Desc>
            </Card>
          ))}
        </Grid>
      </Track>
    </div>
  </Section>
);

export default ProcessSection;
